/**
 * Recovery for lazy chunks that vanished under a running tab.
 *
 * Every build gives its lazy chunks content-hashed filenames, and a deploy
 * replaces the whole set. A tab opened before the deploy still holds the old
 * bundle, so the moment the user navigates to a lazy route it requests a
 * filename that no longer exists on the server:
 *
 *   GET /chunk-6TWIMMST.js  net::ERR_ABORTED 404 (Not Found)
 *
 * The route then fails silently — the app has no handler for this — and the
 * user is left on a screen that will not move until they reload by hand.
 * index.html is served `no-store`, so one reload always picks up the new
 * build; the point of this file is to do that reload for them.
 *
 * Two paths reach here, because a stale build breaks the app in two different
 * ways:
 *
 *  - **Navigation.** The user clicks into a lazy route and the chunk 404s, so
 *    the route never activates and the content area stays blank. Recovering is
 *    safe at any time: by the time the router loads a chunk every
 *    CanDeactivate guard has already run, so the user has accepted leaving the
 *    page and nothing they meant to keep is discarded.
 *
 *  - **Preloading.** `PreloadAllModules` runs right after bootstrap, and
 *    RouterMenuService builds the sidebar from the children Angular stashes on
 *    each lazy route once its chunk loads. A 404 leaves that list empty, and
 *    `router-menu.service.ts` drops any group with no surviving children — so
 *    whole menu sections silently disappear and the app looks half-working
 *    rather than broken. Recovering here is what stops that, but it is only
 *    safe *near startup*: preloading is a background job the user never asked
 *    for, and reloading over it an hour later would throw away whatever they
 *    had typed since. Hence the grace window below — after it, the navigation
 *    path still catches the same staleness the moment it actually blocks
 *    someone.
 */

/** Marks that a reload has already been attempted, so a genuinely broken
 *  deploy cannot put the tab in a reload loop. Session-scoped: the tab that
 *  reloaded is the tab that must not reload again. */
const RELOAD_MARK = "cxa:stale-build-reload";

/** How long a mark suppresses another attempt. Long enough that a failing
 *  reload cannot loop, short enough that a second, unrelated deploy later in
 *  the same session is still recovered. */
const RETRY_WINDOW_MS = 60_000;

/** How long after startup a *preload* failure may still trigger a reload.
 *  Preloading begins immediately after bootstrap, so a genuine stale-build
 *  failure lands well inside this; anything later is not worth a page the user
 *  did not ask to reload. */
const PRELOAD_GRACE_MS = 120_000;

/** Module evaluation time is app start — close enough, and it needs no wiring
 *  into the bootstrap sequence. */
const startedAt = Date.now();

/** Message shapes browsers use for a failed dynamic import, plus the older
 *  bundler wording. Kept broad on purpose: the cost of matching one error too
 *  many is a single reload, and every one of these means the same thing. */
const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "chunkloaderror",
  "loading chunk",
  "loading css chunk",
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const name = (error as { name?: unknown }).name;
  if (typeof name === "string" && name === "ChunkLoadError") return true;

  const message = (error as { message?: unknown }).message;
  const text = (
    typeof message === "string" ? message : String(error)
  ).toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((pattern) => text.includes(pattern));
}

/** Reads the mark. Storage access throws in some privacy modes; treat an
 *  unreadable store as "already marked" so we never reload without being able
 *  to record that we did — an unguarded reload could loop forever. */
function reloadAlreadyAttempted(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_MARK);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < RETRY_WINDOW_MS;
  } catch {
    return true;
  }
}

function markReloadAttempt(): boolean {
  try {
    sessionStorage.setItem(RELOAD_MARK, `${Date.now()}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Preload variant: same recovery, but only while the app is still starting up.
 * Reloads onto the current URL, since the user has not navigated anywhere.
 */
export function recoverFromStalePreload(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (Date.now() - startedAt > PRELOAD_GRACE_MS) return false;

  const { pathname, search, hash } = window.location;
  return recoverFromStaleBuild(error, `${pathname}${search}${hash}`);
}

/**
 * The one real side effect, behind an indirection: jsdom defines
 * `window.location.assign` as non-writable and non-configurable, so a spec
 * cannot observe the navigation any other way. Production code calls through
 * this object exactly as it would call `location.assign` directly.
 */
export const staleBuildNavigation = {
  assign: (url: string): void => window.location.assign(url),
};

/**
 * Reloads the tab at `targetUrl` when `error` is a missing-chunk failure, so
 * the user lands on the page they were navigating to rather than back where
 * they started. Returns whether a reload was triggered.
 */
export function recoverFromStaleBuild(
  error: unknown,
  targetUrl: string,
): boolean {
  if (typeof window === "undefined") return false;
  if (!isChunkLoadError(error)) return false;
  if (reloadAlreadyAttempted()) return false;
  if (!markReloadAttempt()) return false;

  staleBuildNavigation.assign(targetUrl);
  return true;
}
